import { baseApi, TAG } from '../../app/api/baseApi'
import { asArray, asText, toSelectOptions } from './mappingTransforms'
const connectionUrl = (connectorId, instanceId) => `/Connection/${connectorId}/instance/${instanceId}`
const mappingTag = (...parts) => ({ type: TAG.MAPPING, id: parts.join('|') })
const onSuccess = (tag) => (result, error) => (error ? [] : [tag])
const ERP_METHODS_SEGMENT = 'target'
const ECOMMERCE_METHODS_SEGMENT = 'source'
export const METHOD_SEGMENT = { erp: ERP_METHODS_SEGMENT, ecommerce: ECOMMERCE_METHODS_SEGMENT }
const toMethodMapping = (mapping) => ({
    erpMethod: asText(mapping.erpMethod),
    ecommerceMethod: asText(mapping.ecommerceMethod),
    isEcommerceDefault: Boolean(mapping.isEcommerceDefault),
    isErpDefault: Boolean(mapping.isErpDefault)
})
const toMethodRequest = (mapping) => ({
    ecommerceMethod: mapping.ecommerceMethod,
    erpMethod: mapping.erpMethod,
    isEcommerceDefault: mapping.isEcommerceDefault,
    isErpDefault: mapping.isErpDefault
})
const toLocationMapping = (mapping) => ({
    erpLocationId: asText(mapping.erpLocationId),
    erpMethod: asText(mapping.erpMethod),
    ecommerceLocationId: asText(mapping.ecommerceLocationId),
    ecommerceMethod: asText(mapping.ecommerceMethod),
    isDefault: Boolean(mapping.isDefault)
})
const toLocationRequest = (mapping) => ({
    IsDefault: mapping.isDefault,
    EcommerceMethod: mapping.ecommerceMethod,
    ErpMethod: mapping.erpMethod,
    ErpLocationId: mapping.erpLocationId,
    EcommerceLocationId: mapping.ecommerceLocationId,
    isEcommerceDefault: false,
    isErpDefault: false
})
export const ATTRIBUTE_RESOURCE = {
    product: {
        key: 'product',
        getSegment: 'product-attribute',
        saveSegment: 'save-product-mappings',
        erpFieldKeys: ['sourceMethods'],
        ecommerceFieldKeys: ['targetMethods']
    },
    customer: {
        key: 'customer',
        getSegment: 'customer-attribute',
        saveSegment: 'save-customer-mappings',
        erpFieldKeys: ['sourceAttributes', 'sourceMethods'],
        ecommerceFieldKeys: ['targetAttributes', 'targetMethods']
    }
}
export const SOURCE_TO_TARGET = {
    key: 'SourceToTarget',
    requestType: 'Source',
    schemaListKey: 'sourceMappedSchemaList',
    leftIsErp: true
}
export const TARGET_TO_SOURCE = {
    key: 'TargetToSource',
    requestType: 'Target',
    schemaListKey: 'targetMappedSchemaList',
    leftIsErp: false
}
const toField = (method) => ({
    name: asText(method.name),
    dataType: asText(method.dataType),
    isRequired: Boolean(method.isRequired),
    size: asText(method.size),
    type: asText(method.type),
    notAllowed: asText(method.notAllowed).split(',').map((entry) => entry.trim()).filter((entry) => entry !== ''),
    isDefault: Boolean(method.default)
})
const firstList = (data, keys) => {
    const lists = keys.map((key) => asArray(data[key]))
    return lists.find((list) => list.length > 0) || []
}
const toFields = (data, keys) => firstList(data, keys).map(toField).filter((field) => field.name !== '')
const toAttributeMapping = (item) => ({
    erpAttributeName: asText(item.erpAttributeName),
    erpAttributeDataType: asText(item.erpAttributeDataType),
    erpIsRequired: Boolean(item.erpIsRequired),
    erpAttributeSize: asText(item.erpAttributeSize),
    erpAttributeType: asText(item.erpAttributeType),
    ecommerceAttributeName: asText(item.ecommerceAttributeName),
    ecommerceAttributeDataType: asText(item.ecommerceAttributeDataType),
    ecommerceIsRequired: Boolean(item.ecommerceIsRequired),
    ecommerceAttributeSize: asText(item.ecommerceAttributeSize),
    ecommerceAttributeType: asText(item.ecommerceAttributeType),
    default: Boolean(item.default)
})

const toAttributeRequest = (mapping) => ({
    erpAttributeName: mapping.erpAttributeName,
    erpAttributeDataType: mapping.erpAttributeDataType,
    erpIsRequired: mapping.erpIsRequired,
    erpAttributeSize: mapping.erpAttributeSize,
    erpAttributeType: mapping.erpAttributeType,
    ecommerceAttributeName: mapping.ecommerceAttributeName,
    ecommerceAttributeDataType: mapping.ecommerceAttributeDataType,
    ecommerceIsRequired: mapping.ecommerceIsRequired,
    ecommerceAttributeSize: mapping.ecommerceAttributeSize,
    ecommerceAttributeType: mapping.ecommerceAttributeType,
    default: mapping.default,
    canUpdate: true,
    canDelete: true
})
export const SALES_PERSON_SEGMENT = { erp: 'Source', ecommerce: 'Target' }
export const toSalesPersonOptions = (data) => {
    const seen = {}
    return asArray(data).map((salesPerson) => {
        const code = asText(salesPerson.salesPersonCode)
        return { value: code, label: code, id: asText(salesPerson.salesPersonId) }
    })
    .filter((option) => {
        if (option.value === '' || seen[option.value]) {
            return false
        }
        seen[option.value] = true
        return true
    })
}
const toSalesPersonMapping = (mapping) => ({
    erpSalesPersonId: asText(mapping.erpSalesPersonId),
    erpSalesPersonCode: asText(mapping.erpSalesPersonCode),
    ecommerceSalesPersonId: asText(mapping.ecommerceSalesPersonId),
    ecommerceSalesPersonCode: asText(mapping.ecommerceSalesPersonCode)
})
const toSalesPersonRequest = (mapping) => ({
    ecommerceSalesPersonCode: mapping.ecommerceSalesPersonCode,
    erpSalesPersonId: mapping.erpSalesPersonId,
    erpSalesPersonCode: mapping.erpSalesPersonCode,
    ecommerceSalesPersonId: mapping.ecommerceSalesPersonId
})

export const mappingsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getMethodMappings: builder.query({
            async queryFn({ resource, connectorId, instanceId }, api, extraOptions, baseQuery) {
                const base = connectionUrl(connectorId, instanceId)
                const [erp, ecommerce, mappings] = await Promise.all([
                    baseQuery(`${base}/${resource}-methods/${ERP_METHODS_SEGMENT}`),
                    baseQuery(`${base}/${resource}-methods/${ECOMMERCE_METHODS_SEGMENT}`),
                    baseQuery(`${base}/${resource}-mappings`)
                ])
                const failed = [erp, ecommerce, mappings].find((result) => result.error)
                if (failed) {
                    return { error: failed.error }
                }
                return {
                    data: {
                        erpMethods: toSelectOptions(erp.data),
                        ecommerceMethods: toSelectOptions(ecommerce.data),
                        mappings: asArray(mappings.data).map(toMethodMapping)
                    }
                }
            },
            providesTags: (result, error, { resource, connectorId, instanceId }) => [mappingTag(resource, connectorId, instanceId)]
        }),
        saveMethodMappings: builder.mutation({
            query: ({ resource, connectorId, instanceId, mappings }) => ({
                url: `${connectionUrl(connectorId, instanceId)}/${resource}-mappings`,
                method: 'POST',
                body: mappings.map(toMethodRequest)
            }),
            invalidatesTags: (result, error, { resource, connectorId, instanceId }) => onSuccess(mappingTag(resource, connectorId, instanceId))(result, error)
        }),
        getLocationMappings: builder.query({
            async queryFn({ connectorId, instanceId, targetId }, api, extraOptions, baseQuery) {
                const [erp, ecommerce, mappings] = await Promise.all([
                    baseQuery(`/Warehouse/erp/${instanceId}/${targetId}`),
                    baseQuery(`/Warehouse/ecommerce/${instanceId}/${targetId}`),
                    baseQuery(`${connectionUrl(connectorId, instanceId)}/location-mappings`)
                ])
                const failed = [erp, ecommerce, mappings].find((result) => result.error)
                if (failed) {
                    return { error: failed.error }
                }
                return {
                    data: {
                        erpLocations: toSelectOptions(erp.data),
                        ecommerceLocations: toSelectOptions(ecommerce.data),
                        mappings: asArray(mappings.data).map(toLocationMapping)
                    }
                }
            },
            providesTags: (result, error, { connectorId, instanceId }) => [mappingTag('location', connectorId, instanceId)]
        }),
        saveLocationMappings: builder.mutation({
            query: ({ connectorId, instanceId, mappings }) => ({
                url: `${connectionUrl(connectorId, instanceId)}/location-mappings`,
                method: 'POST',
                body: mappings.map(toLocationRequest)
            }),
            invalidatesTags: (result, error, { connectorId, instanceId }) =>
                onSuccess(mappingTag('location', connectorId, instanceId))(result, error)
        }),
        getAttributeMappings: builder.query({
            query: ({ resource, connectorId, instanceId }) =>
                `${connectionUrl(connectorId, instanceId)}/${ATTRIBUTE_RESOURCE[resource].getSegment}`,
            transformResponse: (data, meta, { resource }) => {
                const config = ATTRIBUTE_RESOURCE[resource]
                const body = data || {}
                return {
                    erpFields: toFields(body, config.erpFieldKeys),
                    ecommerceFields: toFields(body, config.ecommerceFieldKeys),
                    mappingsByDirection: {
                        [SOURCE_TO_TARGET.schemaListKey]: asArray(body[SOURCE_TO_TARGET.schemaListKey]).map(toAttributeMapping),
                        [TARGET_TO_SOURCE.schemaListKey]: asArray(body[TARGET_TO_SOURCE.schemaListKey]).map(toAttributeMapping)
                    }
                }
            },
            providesTags: (result, error, { resource, connectorId, instanceId }) => [mappingTag(resource, connectorId, instanceId)]
        }),
        saveAttributeMappings: builder.mutation({
            query: ({ resource, connectorId, instanceId, direction, mappings }) => ({
                url: `${connectionUrl(connectorId, instanceId)}/${ATTRIBUTE_RESOURCE[resource].saveSegment}/${direction.requestType}`,
                method: 'POST',
                body: { [direction.schemaListKey]: mappings.map(toAttributeRequest) }
            }),
            invalidatesTags: (result, error, { resource, connectorId, instanceId }) =>
                onSuccess(mappingTag(resource, connectorId, instanceId))(result, error)
        }),
        getSalesPersonMappings: builder.query({
            async queryFn({ connectorId }, api, extraOptions, baseQuery) {
                const [erp, ecommerce, mappings] = await Promise.all([
                    baseQuery(`/Connection/${connectorId}/salesPersonCode/${SALES_PERSON_SEGMENT.erp}`),
                    baseQuery(`/Connection/${connectorId}/salesPersonCode/${SALES_PERSON_SEGMENT.ecommerce}`),
                    baseQuery(`/Connection/${connectorId}/salesPerson-mappings`)
                ])
                const failed = [erp, ecommerce, mappings].find((result) => result.error)
                if (failed) {
                    return { error: failed.error }
                }
                return {
                    data: {
                        erpSalesPersons: toSalesPersonOptions(erp.data),
                        ecommerceSalesPersons: toSalesPersonOptions(ecommerce.data),
                        mappings: asArray(mappings.data).map(toSalesPersonMapping)
                    }
                }
            },
            providesTags: (result, error, { connectorId }) => [mappingTag('salesPerson', connectorId)]
        }),
        saveSalesPersonMappings: builder.mutation({
            query: ({ connectorId, mappings }) => ({
                url: `/Connection/${connectorId}/salesPerson-mappings`,
                method: 'POST',
                body: mappings.map(toSalesPersonRequest)
            }),
            invalidatesTags: (result, error, { connectorId }) =>
                (result && result.result ? [mappingTag('salesPerson', connectorId)] : [])
        })
    })
})

export const {
    useGetMethodMappingsQuery,
    useSaveMethodMappingsMutation,
    useGetLocationMappingsQuery,
    useSaveLocationMappingsMutation,
    useGetAttributeMappingsQuery,
    useSaveAttributeMappingsMutation,
    useGetSalesPersonMappingsQuery,
    useSaveSalesPersonMappingsMutation
} = mappingsApi
